import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AgentBaseInfo } from '@agents/models/agent-base-info';
import { AgentsStoreService } from '@agents/store/services/agents-store.service';
import { AppointmentsStoreService } from '@appointments/store/services/appointments-store.service';
import { CollaborationSpaceStoreService } from '@auth/store/services/collaboration-space-store.service';
import { UserStoreService } from '@auth/store/services/user-store.service';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { RouterStoreService } from '@core-layout/app/store/services/router-store.service';
import { ListingsStoreService } from '@listings/store/services/listings-store.service';
import { NotificationsStoreService } from '@notifications/store/services/notifications-store.service';
import { SettingsStoreService } from '@settings/store/services/settings-store.service';
import { AvatarInfo } from '@users/models/avatar-info';
import { PROFILE_TABS_NAMES } from 'app/modules/user-modules/profile-modules/profile-base/constants/profile-tabs.constants';
import { ProfileBaseStoreService } from 'app/modules/user-modules/profile-modules/profile-base/store/services/profile-base-store.service';
import { PortfolioStoreService } from '@portfolio/store/services/portfolio-store.service';
import { BADGE_ALERT_ANIMATION_CONFIG } from './toolbar.constants';
import { BADGE_COUNT_ALERT_LIMIT, NOTIFICATION_TYPE_NOTIFICATION_BELL_TAB_MAP } from '@notifications/constants/notifications.constants';
import { NotificationNavigateParams } from '@notifications/models/notification-navigate-params';
import { ListingCommunicationsService } from 'app/modules/listing-modules/listing-communications/listing-communications-base/services/listing-communications.service';
import { SavedSearchStoreService } from '@saved-search/store/services/saved-search-store.service';
import { OnMarketStoreService } from '@on-market/store/services/on-market-store.service';
import { NavigationItem } from '@listing-details-navigation/models/navigation-item';
import { InternalListingDetailsNavigationService } from '@listing-details-navigation/services/listing-details-navigation.service';
import { RpcDropdownDirective } from '@core-directives/rpc-dropdown/rpc-dropdown.directive';
import { LogoComponent } from '../logo/logo.component';
import { RpcButtonComponent } from '../../controls/components/rpc-button/rpc-button.component';
import { RouterLinkActive } from '@angular/router';
import { PortfolioAgentsListComponent } from '../../../user-modules/agents/components/portfolio-agents-list/portfolio-agents-list.component';
import { RpcAvatarComponent } from '../../controls/components/rpc-avatar/rpc-avatar.component';
import { AvatarsIconComponent } from '../../../user-modules/users/components/avatars-icon/avatars-icon.component';
import { NotificationsComponent } from '../../../notifications/components/notifications/notifications.component';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        LogoComponent,
        RpcButtonComponent,
        RouterLinkActive,
        RpcDropdownDirective,
        PortfolioAgentsListComponent,
        RpcAvatarComponent,
        AvatarsIconComponent,
        NotificationsComponent,
        AsyncPipe,
        TranslateModule
    ]
})
export class ToolbarComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsDropdown') public readonly notificationsDropdown: RpcDropdownDirective<unknown>;

    public readonly RpcRoute = RpcRoute;
    public readonly isUserAuthorized$ = this.userStoreService.getIsAuthorized();
    public readonly badgeCountAlertLimit = BADGE_COUNT_ALERT_LIMIT;
    public readonly canSearchForListings$ = this.settingsStoreService.canSearchForListings$;
    public readonly appointmentsCount$ = this.appointmentsStoreService.getFlatListingsAppointmentsCount();
    public readonly unviewedAppointmentsCount$ = this.appointmentsStoreService.unviewedAppointmentsCount$;
    public readonly unviewedListingsCount$ = this.listingsStoreService.unviewedListingsCount$;
    public readonly customerFullName$ = this.userStoreService.getCustomerFullName();
    public readonly customerProfileImage$ = this.userStoreService.getCustomerProfileImage();
    public readonly hideLoginRedirect$ = this.routeService.getCurrentRouteDataProperty<boolean>('hideToolbarLoginRedirect');
    public readonly canSeeAdditionalProfiles$ = this.userStoreService.canSeeAdditionalProfiles$;
    public readonly canAddAdditionalProfile$ = this.profileBaseStoreService.canAddAdditionalProfile$;
    public readonly hasMultipleCollaborationSpaces$ = this.collaborationSpaceStoreService.hasMultipleCollaborationSpaces$;
    public readonly agents$ = this.agentsStoreService.getAgents();
    public readonly isKnowledgeBaseOpened$ = this.routerStoreService.url$.pipe(map(url => url != null && url.includes(RpcRoute.KnowledgeBase)));
    public readonly portfolioRouterLink$ = this.portfolioStoreService.portfolioRouterLink$;
    public readonly unviewedNotificationTotalCount$ = this.notificationsStoreService.unviewedNotificationTotalCount$;
    public readonly badgeAlertAnimationConfig = BADGE_ALERT_ANIMATION_CONFIG;
    public readonly savedSearchesInfo$ = this.savedSearchStoreService.savedSearchesInfo$;
    public readonly isSettingsLoading$ = this.settingsStoreService.isLoading$;

    public readonly isFindHomeActive$ = this.routerStoreService.url$.pipe(
        map(url => url != null && url.slice(1) === RpcRoute.SearchListings)
    );

    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private readonly routeService: RouteService,
        private readonly listingsStoreService: ListingsStoreService,
        private readonly appointmentsStoreService: AppointmentsStoreService,
        private readonly userStoreService: UserStoreService,
        private readonly notificationsStoreService: NotificationsStoreService,
        private readonly settingsStoreService: SettingsStoreService,
        private readonly profileBaseStoreService: ProfileBaseStoreService,
        private readonly collaborationSpaceStoreService: CollaborationSpaceStoreService,
        private readonly agentsStoreService: AgentsStoreService,
        private readonly routerStoreService: RouterStoreService,
        private readonly portfolioStoreService: PortfolioStoreService,
        private readonly listingCommunicationsService: ListingCommunicationsService,
        private readonly savedSearchStoreService: SavedSearchStoreService,
        private readonly onMarketStoreService: OnMarketStoreService,
        private readonly internalListingDetailsNavigationService: InternalListingDetailsNavigationService<NavigationItem>,
    ) { }

    public ngOnInit(): void {
        this.listingCommunicationsService.openNotificationBellTab$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(({ notificationType }) => {
                const activeTab = NOTIFICATION_TYPE_NOTIFICATION_BELL_TAB_MAP.get(notificationType);

                this.notificationsStoreService.setActiveTab(activeTab);

                this.notificationsDropdown.open();
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public onLogoClicked(): void {
        this.routeService.navigate(RpcRoute.FindHome).catch(() => { });
    }

    public onMyProfileClicked(): void {
        this.routeService.navigate(RpcRoute.Profile, { state: { tab: PROFILE_TABS_NAMES.GENERAL } }).catch(() => { });
    }

    public onSettingsClicked(): void {
        this.routeService.navigate(RpcRoute.Profile, { state: { tab: PROFILE_TABS_NAMES.SETTINGS } }).catch(() => { });
    }

    public onInviteUser(): void {
        this.profileBaseStoreService.showInviteUserDialog();
    }

    public onLogoutClicked(): void {
        this.userStoreService.logout(false);
    }

    public onChangeAgentClicked(): void {
        this.collaborationSpaceStoreService.loadCollaborationSpaces();
    }

    public onKnowledgeBaseClicked(): void {
        this.routeService.navigate(RpcRoute.KnowledgeBase).catch(() => { });
    }

    public getAgentAvatarInfo(agent: AgentBaseInfo): AvatarInfo {
        return { fullName: agent.fullName, profileImage: agent.profilePictureUrl };
    }

    public onNotificationSourceNavigated(notificationNavigateParams: NotificationNavigateParams): void {
        this.notificationsDropdown.close();

        this.notificationsStoreService.setActiveTab(null);
        this.onMarketStoreService.resetState();
        this.notificationsStoreService.redirectToNotificationEntity(notificationNavigateParams);
    }

    public onNotificationBellClosed(): void {
        this.notificationsStoreService.setActiveTab(null);
    }

    public onFindHomeClick(isFindHomeActive: boolean): void {
        if (isFindHomeActive) {
            return;
        }

        this.onMarketStoreService.resetState();
        this.internalListingDetailsNavigationService.setNavigation();

        this.routeService.navigate(RpcRoute.SearchListings).catch(() => { });
    }

    public openSavedSearchesList(): void {
        this.savedSearchStoreService.showSavedSearchList(false);
    }
}